import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import AdForgeLogo from '../../components/feature/AdForgeLogo';

export default function ResetPasswordPage() {
  const { updatePassword } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    const readAuthCallbackError = () => {
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return search.get('error_description')
        ?? hash.get('error_description')
        ?? search.get('error')
        ?? hash.get('error');
    };

    const hasAuthCallbackParams = () => {
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return search.has('code') || hash.has('access_token') || hash.has('refresh_token') || hash.get('type') === 'recovery';
    };

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setChecking(false);
      } else if (event === 'INITIAL_SESSION' && session && hasAuthCallbackParams()) {
        setIsValidSession(true);
        setChecking(false);
      }
    });

    const validateRecoverySession = async () => {
      const callbackError = readAuthCallbackError();
      if (callbackError) {
        setError(callbackError);
        setChecking(false);
        return;
      }

      const hasCallbackParams = hasAuthCallbackParams();
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;

      if (session) {
        setIsValidSession(true);
        setChecking(false);
        return;
      }

      const code = new URLSearchParams(window.location.search).get('code');
      if (hasCallbackParams && code) {
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (!mounted) return;
        if (exchangeError) {
          setError(exchangeError.message);
        } else if (data.session) {
          setIsValidSession(true);
        }
      }

      setChecking(false);
    };

    validateRecoverySession().catch((sessionError) => {
      console.error('Failed to validate password recovery session', sessionError);
      if (mounted) {
        setError('We could not validate this reset link. Please request a new one.');
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const getPasswordStrength = (pwd: string): { level: number; label: string; color: string } => {
    if (pwd.length === 0) return { level: 0, label: '', color: '' };
    if (pwd.length < 6) return { level: 1, label: 'Too short', color: 'bg-rose-400' };
    if (pwd.length < 8) return { level: 2, label: 'Weak', color: 'bg-orange-400' };
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
    const score = [hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
    if (score === 3) return { level: 4, label: 'Strong', color: 'bg-emerald-500' };
    if (score >= 1) return { level: 3, label: 'Good', color: 'bg-amber-400' };
    return { level: 2, label: 'Weak', color: 'bg-orange-400' };
  };

  const strength = getPasswordStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setError('');
    setIsLoading(true);
    const { error: authError } = await updatePassword(password).catch((passwordError) => ({
      error: passwordError instanceof Error ? passwordError.message : 'Unable to update password.',
    }));
    setIsLoading(false);
    if (authError) {
      setError(authError);
    } else {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 font-sans flex">
      <div className="hidden lg:flex lg:w-1/2 bg-stone-950 flex-col justify-between p-12">
        <Link to="/">
          <AdForgeLogo size="md" light={true} />
        </Link>
        <div>
          <h2 className="text-3xl font-extrabold text-white mb-4 leading-snug">
            Almost there — <br />create your new password.
          </h2>
          <p className="text-stone-400 text-sm leading-relaxed max-w-xs">
            Choose something strong. A mix of letters, numbers, and symbols works best.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className="ri-star-fill text-amber-400 text-sm" />
            ))}
          </div>
          <span className="text-sm text-stone-400">Rated 4.9/5 by 3,200+ businesses</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Link to="/">
              <AdForgeLogo size="lg" light={false} />
            </Link>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-50 mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-3xl text-emerald-500" />
              </div>
              <h1 className="text-2xl font-extrabold text-stone-900 mb-2">Password updated!</h1>
              <p className="text-sm text-stone-500 mb-6 leading-relaxed">
                Your password has been successfully reset. Redirecting you to sign in...
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:underline cursor-pointer"
              >
                Go to sign in now
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ) : !isValidSession ? (
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-rose-50 mx-auto mb-5">
                <i className="ri-error-warning-line text-3xl text-rose-500" />
              </div>
              <h1 className="text-2xl font-extrabold text-stone-900 mb-2">Link expired or invalid</h1>
              <p className="text-sm text-stone-500 mb-6 leading-relaxed">
                This reset link is no longer valid. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Request new link
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Set a new password</h1>
              <p className="text-sm text-stone-500 mb-8">
                Must be at least 6 characters.
              </p>

              {error && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full text-sm px-4 py-3 pr-11 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
                    >
                      <i className={`ri-${showPass ? 'eye-off' : 'eye'}-line text-sm`} />
                    </button>
                  </div>
                  {password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                              i < strength.level ? strength.color : 'bg-stone-200'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-stone-400">{strength.label}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-700 mb-1.5">Confirm new password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full text-sm px-4 py-3 pr-11 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
                    >
                      <i className={`ri-${showConfirm ? 'eye-off' : 'eye'}-line text-sm`} />
                    </button>
                  </div>
                  {confirmPassword.length > 0 && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-rose-500">Passwords do not match</p>
                  )}
                  {confirmPassword.length > 0 && password === confirmPassword && (
                    <p className="mt-1 text-xs text-emerald-500 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line" /> Passwords match
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70"
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin" /> Updating...</>
                  ) : (
                    <><i className="ri-lock-password-line" /> Update Password</>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
